import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UploadSimple, Lock, User, SealCheck, Eye } from "@phosphor-icons/react";
import VerifiedBadge from "@/components/VerifiedBadge";
import UserBadge from "@/components/UserBadge";
import AchievementBadges from "@/components/AchievementBadges";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ full_name: "", country: "", timezone: "UTC", bio: "", cashtag: "", venmo: "", onepay: "", paypal: "", applepay: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [avatar, setAvatar] = useState("");
  const [verState, setVerState] = useState(null);
  const [verReason, setVerReason] = useState("");
  const [verLink, setVerLink] = useState("");
  const [verBusy, setVerBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name || "",
        country: user.country || "",
        timezone: user.timezone || "UTC",
        bio: user.bio || "",
        cashtag: user.cashtag || "",
        venmo: user.venmo || "",
        onepay: user.onepay || "",
        paypal: user.paypal || "",
        applepay: user.applepay || "",
      });
      setAvatar(user.avatar_url || "");
    }
    api.get("/verification/mine").then((r) => setVerState(r.data)).catch(() => {});
  }, [user]);

  const submitVerification = async (e) => {
    e.preventDefault();
    if (verReason.trim().length < 10) return toast.error("Please add at least 10 characters.");
    setVerBusy(true);
    try {
      await api.post("/verification/request", { reason: verReason.trim(), social_link: verLink.trim() });
      toast.success("Verification request submitted — an admin will review shortly.");
      const r = await api.get("/verification/mine");
      setVerState(r.data);
      setVerReason(""); setVerLink("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setVerBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/profile", form);
      toast.success("Profile updated");
      refresh();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatar(data.avatar_url);
      toast.success("Avatar updated");
      refresh();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    try {
      await api.post("/profile/change-password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <DashboardLayout>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Account</div>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>My Profile</h1>
        </div>
        {user?.id && (
          <Link
            to={`/u/${user.id}`}
            data-testid="view-my-profile-btn"
            className="inline-flex items-center gap-2 rounded-full bg-blue-700 hover:bg-blue-800 text-white font-black px-4 py-2 shadow-sm transition-colors duration-200"
          >
            <Eye size={16} weight="bold" />
            View my public profile
          </Link>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-1 space-y-4">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 text-center">
            <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white text-3xl font-black inline-flex items-center justify-center overflow-hidden">
              {avatar ? <img src={`${backend}${avatar}`} alt="avatar" className="h-full w-full object-cover" /> : (user?.username || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="mt-3 font-black text-slate-900 dark:text-white text-lg inline-flex items-center gap-1 flex-wrap justify-center" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
              @{user?.username}
              <UserBadge user={user} size={16} />
              <AchievementBadges userId={user?.id} size={16} />
            </div>
            <div className="text-xs text-slate-500">Joined {user?.created_at ? new Date(user.created_at).toLocaleDateString() : ""}</div>
            <label className="mt-3 inline-flex items-center gap-2 cursor-pointer rounded-full bg-blue-700 hover:bg-blue-800 text-white font-bold px-4 py-2 text-sm transition-colors duration-200">
              <UploadSimple size={14} weight="bold" /> Upload photo
              <input data-testid="avatar-upload-input" type="file" accept="image/*" className="hidden" onChange={uploadAvatar} />
            </label>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Membership</div>
            <div className="text-2xl font-black text-slate-900 dark:text-white mt-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{user?.membership}</div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-3">Sites surfed</div>
            <div data-testid="profile-total-surfs" className="text-2xl font-black text-slate-900 dark:text-white mt-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{Number(user?.total_surfs || 0).toLocaleString()}</div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-3">Referral code</div>
            <div className="font-mono text-blue-700 dark:text-blue-400">{user?.referral_code}</div>
          </div>
        </div>

        <div className="md:col-span-2 space-y-4">
          <form onSubmit={save} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-3">
            <div className="flex items-center gap-2 mb-1"><User size={18} weight="bold" /><h2 className="font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Profile details</h2></div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Full name</label>
                <input data-testid="profile-fullname-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Country</label>
                <input data-testid="profile-country-input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Timezone</label>
                <input data-testid="profile-tz-input" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Cash App Cashtag</label>
                <input data-testid="profile-cashtag-input" placeholder="$yourtag" value={form.cashtag} onChange={(e) => setForm({ ...form, cashtag: e.target.value })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Venmo</label>
                <input data-testid="profile-venmo-input" placeholder="@yourhandle" value={form.venmo} onChange={(e) => setForm({ ...form, venmo: e.target.value })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">OnePay</label>
                <input data-testid="profile-onepay-input" placeholder="your@onepay.id" value={form.onepay} onChange={(e) => setForm({ ...form, onepay: e.target.value })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">PayPal</label>
                <input data-testid="profile-paypal-input" placeholder="you@paypal.com or paypal.me/you" value={form.paypal} onChange={(e) => setForm({ ...form, paypal: e.target.value })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Apple Pay</label>
                <input data-testid="profile-applepay-input" placeholder="Phone or email tied to Apple Pay" value={form.applepay} onChange={(e) => setForm({ ...form, applepay: e.target.value })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Biography</label>
              <textarea data-testid="profile-bio-input" rows="3" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
            </div>
            <button data-testid="profile-save-btn" className="rounded-full bg-blue-700 hover:bg-blue-800 text-white font-bold px-5 py-2 transition-colors duration-200">Save changes</button>
          </form>

          {/* Verification */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <VerifiedBadge size={20} />
              <h2 className="font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Account verification</h2>
            </div>
            {user?.verified ? (
              <div className="flex items-start gap-3 rounded-xl bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 p-3">
                <UserBadge user={user} size={22} className="mt-0.5" />
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">You&apos;re {user?.role === "admin" ? "an administrator" : "verified"}</div>
                  <div className="text-sm text-slate-600 dark:text-slate-300">Your {user?.role === "admin" ? "gold" : "blue"} check appears next to your name across the platform. Hover over it anywhere to see what it means.</div>
                </div>
              </div>
            ) : user?.role === "admin" ? (
              <div className="flex items-start gap-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 p-3">
                <UserBadge user={user} size={22} className="mt-0.5" />
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">You&apos;re an administrator</div>
                  <div className="text-sm text-slate-600 dark:text-slate-300">Your gold check appears next to your name everywhere on the site.</div>
                </div>
              </div>
            ) : verState?.request?.status === "pending" ? (
              <div className="rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 p-3 text-sm text-amber-800 dark:text-amber-200">
                Your verification request is pending admin review. We&apos;ll notify you when it&apos;s decided.
              </div>
            ) : (
              <form onSubmit={submitVerification} className="space-y-2" data-testid="verification-request-form">
                {verState?.request?.status === "rejected" && (
                  <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 p-3 text-sm text-rose-800 dark:text-rose-200">
                    Your previous request was declined. You may submit a new one below.
                  </div>
                )}
                <p className="text-sm text-slate-600 dark:text-slate-400">Request a blue checkmark for your account. Please tell us why your account should be verified (e.g., established business, public figure, brand identity).</p>
                <textarea
                  data-testid="verification-reason-input"
                  rows="3"
                  minLength={10}
                  required
                  value={verReason}
                  onChange={(e) => setVerReason(e.target.value)}
                  placeholder="Briefly explain who you are and why you should be verified..."
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 text-sm"
                />
                <input
                  data-testid="verification-link-input"
                  type="url"
                  value={verLink}
                  onChange={(e) => setVerLink(e.target.value)}
                  placeholder="Optional: link to your website / social profile"
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 text-sm"
                />
                <button
                  type="submit"
                  disabled={verBusy}
                  data-testid="verification-submit-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-bold px-5 py-2 text-sm transition-colors duration-200"
                >
                  <SealCheck size={14} weight="bold" /> {verBusy ? "Submitting..." : "Request verification"}
                </button>
              </form>
            )}
          </div>

          <form onSubmit={changePw} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-3">
            <div className="flex items-center gap-2 mb-1"><Lock size={18} weight="bold" /><h2 className="font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Change password</h2></div>
            <div className="grid md:grid-cols-2 gap-3">
              <input data-testid="pw-current-input" type="password" required placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
              <input data-testid="pw-new-input" type="password" required minLength={6} placeholder="New password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100" />
            </div>
            <button data-testid="pw-save-btn" className="rounded-full bg-slate-900 dark:bg-slate-700 text-white font-bold px-5 py-2">Update password</button>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}
